import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FileCheck2, Sparkles, ExternalLink, ShieldCheck, Plus, Trash2, CalendarClock,
  Mail, Copy, Check, ChevronDown, AlertTriangle, Pencil, Send,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { format, parseISO } from "date-fns";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const REQ_STATUSES = ["MISSING", "REQUESTED", "RECEIVED", "SUBMITTED"];
const DRAFT_TYPES = ["REPLY", "FOLLOWUP", "WIDERSPRUCH"];
const BASIS_COLORS = {
  OFFICIAL_DEADLINE: { bg: "#FDF2F0", c: "#C03A2B" },
  VERIFIED_PROCESSING: { bg: "#EDF7F2", c: "#1B6943" },
  USER_PLANNING: { bg: "#EBF3FF", c: "#1E5299" },
  AI_ESTIMATE: { bg: "#FFF8EC", c: "#B36B00" },
  UNKNOWN: { bg: "#F1F3F2", c: "#5A625E" },
};
const VERIF_COLORS = {
  USER_CONFIRMED: { bg: "#EDF7F2", c: "#1B6943" },
  NOT_VERIFIED: { bg: "#FFF8EC", c: "#B36B00" },
  UNKNOWN: { bg: "#F1F3F2", c: "#5A625E" },
};

function Pill({ children, style, testid }) {
  return (
    <span data-testid={testid} className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={style}>
      {children}
    </span>
  );
}

const fmtDate = (s) => { try { return s ? format(parseISO(s), "dd.MM.yyyy") : ""; } catch { return s; } };

// ---------------- Required Documents ----------------
function RequiredDocs({ caseData }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const cid = caseData.id;
  const [name, setName] = useState("");
  const [inst, setInst] = useState("");
  const [plz, setPlz] = useState("");
  const [busy, setBusy] = useState(null);

  const { data: docs = [], isLoading: reqLoading } = useQuery({
    queryKey: ["reqdocs", cid, lang],
    queryFn: async () => (await api.get(`/cases/${cid}/required-documents?language=${lang}`)).data,
  });
  const [autoTried, setAutoTried] = useState(false);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["reqdocs", cid] }); qc.invalidateQueries({ queryKey: ["events", cid] }); };

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post(`/cases/${cid}/required-documents`, { name, issuing_institution: inst || null, plz: plz || null });
      setName(""); setInst(""); setPlz(""); refresh();
    } catch (e) { toast.error(apiError(e, t)); }
  };
  const importDocs = async () => {
    let total = 0, failed = 0;
    for (const id of caseData.document_ids || []) {
      try { const r = await api.post(`/cases/${cid}/required-documents/import/${id}`); total += r.data.created; }
      catch (_) { failed += 1; }
    }
    if (failed && !total) toast.error(t("errors.generic"));
    else toast.success(`+${total}`);
    refresh();
  };
  // Auto-propose grounded required documents from the case's analyses (no manual button needed).
  useEffect(() => {
    if (autoTried || reqLoading) return;
    if ((caseData.document_ids || []).length && docs.length === 0) {
      setAutoTried(true);
      (async () => {
        for (const id of caseData.document_ids || []) {
          try { await api.post(`/cases/${cid}/required-documents/import/${id}`); } catch (_) { /* ignore */ }
        }
        refresh();
      })();
    }
  }, [reqLoading, docs.length, caseData.document_ids, autoTried, cid]);
  const suggest = async (rid) => {
    setBusy(rid);
    try { await api.post(`/required-documents/${rid}/suggest`, { ui_language: lang }); refresh(); }
    catch (e) { toast.error(apiError(e, t)); } finally { setBusy(null); }
  };
  const patch = async (rid, body) => {
    try { await api.patch(`/required-documents/${rid}`, body); refresh(); }
    catch (e) { toast.error(apiError(e, t)); }
  };
  const remove = async (rid) => { try { await api.delete(`/required-documents/${rid}`); refresh(); } catch (e) { toast.error(apiError(e, t)); } };

  return (
    <div data-testid="e4-required-docs" className="mt-4 space-y-3">
      {(caseData.document_ids || []).length > 0 && docs.length === 0 && (
        <button data-testid="e4-import-docs" onClick={importDocs}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#C4DFD2] bg-[#F2F7F4] py-2.5 text-sm font-semibold text-[#2D5A4C] active:scale-[0.99]">
          <FileCheck2 size={15} /> {t("e4.importFromDocs")}
        </button>
      )}

      {docs.length === 0 ? (
        <p className="rounded-xl border border-[#E2EAE5] bg-white/60 px-3 py-3 text-xs text-[#B0C2B8]">{t("e4.none")}</p>
      ) : docs.map((d) => {
        const src = d.official_source || {};
        const vc = VERIF_COLORS[src.verification_status] || VERIF_COLORS.UNKNOWN;
        return (
          <div key={d.id} data-testid={`e4-reqdoc-${d.id}`} className="rounded-2xl border border-[#E2EAE5] bg-white p-3.5">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-sm font-bold text-[#1A2E26]" dir="auto">{d.name}</p>
                  {d.grounding && <Pill style={{ backgroundColor: "#EDF7F2", color: "#1B6943" }}>{t("e4.basis.DOCUMENT")}</Pill>}
                </div>
                {d.issuing_institution && <p className="mt-0.5 text-xs text-[#7A9488]" dir="auto">{d.issuing_institution}</p>}
              </div>
              <button data-testid={`e4-reqdoc-delete-${d.id}`} onClick={() => remove(d.id)} className="rounded-full p-1.5 text-[#C03A2B] hover:bg-[#FDF2F0]"><Trash2 size={15} /></button>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Select value={d.status} onValueChange={(v) => patch(d.id, { status: v })}>
                <SelectTrigger data-testid={`e4-reqdoc-status-${d.id}`} className="h-8 w-auto gap-1 rounded-full border-[#E2EAE5] px-3 text-xs font-semibold"><SelectValue /></SelectTrigger>
                <SelectContent>{REQ_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`e4.reqStatus.${s}`)}</SelectItem>)}</SelectContent>
              </Select>
              <Pill testid={`e4-reqdoc-appt-${d.id}`} style={{ backgroundColor: "#F0F4F1", color: "#4A6358" }}>
                {t("e4.appointment")}: {t(`e4.appt.${d.appointment_status || "UNKNOWN"}`)}
              </Pill>
              {d.appointment_status_basis && <Pill style={{ backgroundColor: "#F1F3F2", color: "#5A625E" }}>{t(`e4.basis.${d.appointment_status_basis}`)}</Pill>}
              <Pill testid={`e4-reqdoc-verif-${d.id}`} style={{ backgroundColor: vc.bg, color: vc.c }}>
                {t("e4.officialSource")}: {t(`e4.verif.${src.verification_status || "UNKNOWN"}`)}
              </Pill>
            </div>

            {d.how_to_obtain && (
              <div className="mt-2 rounded-xl bg-[#F8FAF8] p-2.5">
                <div className="mb-0.5 flex items-center gap-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#7A9488]">{t("e4.howToObtain")}</span>
                  {d.how_to_obtain_basis && <Pill style={{ backgroundColor: "#F1F3F2", color: "#5A625E" }}>{t(`e4.basis.${d.how_to_obtain_basis}`)}</Pill>}
                </div>
                <p className="text-xs text-[#4A6358]" dir="auto">{d.how_to_obtain}</p>
              </div>
            )}

            {(src.url || src.authority) && (
              <div className="mt-2 rounded-xl border border-[#E2EAE5] p-2.5">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#7A9488]">{t("e4.officialSource")}</span>
                  <Pill style={{ backgroundColor: vc.bg, color: vc.c }}>{t(`e4.verif.${src.verification_status || "UNKNOWN"}`)}</Pill>
                </div>
                {src.authority && <p className="text-xs font-semibold text-[#1A2E26]" dir="auto">{src.authority}</p>}
                {src.url && (
                  <a data-testid={`e4-reqdoc-link-${d.id}`} href={src.url} target="_blank" rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 break-all text-xs font-medium text-[#2D5A4C] underline">
                    <ExternalLink size={12} /> {src.url}
                  </a>
                )}
                {src.verification_status === "NOT_VERIFIED" && (
                  <button data-testid={`e4-reqdoc-confirm-${d.id}`} onClick={() => patch(d.id, { verification_status: "USER_CONFIRMED" })}
                    className="mt-2 flex items-center gap-1.5 rounded-full bg-[#2D5A4C] px-3 py-1.5 text-xs font-semibold text-white active:scale-95">
                    <ShieldCheck size={13} /> {t("e4.confirmSource")}
                  </button>
                )}
              </div>
            )}
            {d.appointment_url && (
              <a href={d.appointment_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#2D5A4C] underline">
                <CalendarClock size={12} /> {t("e4.appointment")}
              </a>
            )}

            <button data-testid={`e4-reqdoc-suggest-${d.id}`} onClick={() => suggest(d.id)} disabled={busy === d.id}
              className="mt-2.5 flex items-center gap-1.5 rounded-full border border-[#C4DFD2] px-3 py-1.5 text-xs font-semibold text-[#2D5A4C] active:scale-95 disabled:opacity-50">
              <Sparkles size={13} /> {busy === d.id ? t("e4.suggesting") : t("e4.aiSuggest")}
            </button>
          </div>
        );
      })}

      <div className="rounded-2xl border border-dashed border-[#E2EAE5] p-3">
        <Input data-testid="e4-newdoc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("e4.docName")} className="mb-2 h-10 rounded-xl" />
        <div className="mb-2 flex gap-2">
          <Input data-testid="e4-newdoc-inst" value={inst} onChange={(e) => setInst(e.target.value)} placeholder={t("e4.institution")} className="h-10 rounded-xl" />
          <Input data-testid="e4-newdoc-plz" value={plz} onChange={(e) => setPlz(e.target.value)} placeholder={t("e4.plz")} className="h-10 w-24 rounded-xl" />
        </div>
        <button data-testid="e4-newdoc-add" onClick={add} className="flex items-center gap-1.5 rounded-full bg-[#2D5A4C] px-3.5 py-2 text-sm font-semibold text-white active:scale-95">
          <Plus size={15} /> {t("e4.addDoc")}
        </button>
      </div>
    </div>
  );
}

// ---------------- Backward Plan ----------------
function PlanSection({ caseData }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const cid = caseData.id;
  const [busy, setBusy] = useState(false);

  const { data: plan = { steps: [] } } = useQuery({
    queryKey: ["plan", cid],
    queryFn: async () => (await api.get(`/cases/${cid}/plan`)).data,
  });

  const gen = async () => {
    setBusy(true);
    try { await api.post(`/cases/${cid}/plan`, { ui_language: lang }); qc.invalidateQueries({ queryKey: ["plan", cid] }); qc.invalidateQueries({ queryKey: ["events", cid] }); }
    catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };
  const promote = async (step_id, as_type) => {
    try {
      await api.post(`/cases/${cid}/plan/promote`, { step_id, as_type });
      toast.success(t("e4.promoted"));
      qc.invalidateQueries({ queryKey: ["plan", cid] });
      qc.invalidateQueries({ queryKey: ["actions", cid] });
      qc.invalidateQueries({ queryKey: ["deadlines", cid] });
      qc.invalidateQueries({ queryKey: ["events", cid] });
    } catch (e) { toast.error(apiError(e, t)); }
  };

  return (
    <div data-testid="e4-plan" className="mt-4">
      {plan.official_deadline && (
        <p className="mb-2 text-xs text-[#7A9488]">{t("e4.officialDeadline")}: <span className="font-mono font-semibold text-[#1A2E26]">{fmtDate(plan.official_deadline)}</span></p>
      )}
      <button data-testid="e4-plan-generate" onClick={gen} disabled={busy}
        className="mb-3 flex items-center gap-1.5 rounded-full bg-[#2D5A4C] px-3.5 py-2 text-sm font-semibold text-white active:scale-95 disabled:opacity-50">
        <Sparkles size={15} /> {busy ? t("common.saving") : (plan.steps?.length ? t("e4.regeneratePlan") : t("e4.generatePlan"))}
      </button>
      {plan.steps?.length > 0 && <p className="mb-2 text-[11px] italic text-[#B0C2B8]">{t("e4.planNote")}</p>}
      <div className="space-y-2">
        {(plan.steps || []).map((s) => {
          const bc = BASIS_COLORS[s.date_basis] || BASIS_COLORS.UNKNOWN;
          return (
            <div key={s.step_id} data-testid={`e4-plan-step-${s.step_id}`} className="rounded-2xl border border-[#E2EAE5] bg-white p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-[#1A2E26]" dir="auto">{s.title}</p>
                {s.target_date && <span className="shrink-0 font-mono text-xs text-[#4A6358]">{fmtDate(s.target_date)}</span>}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Pill style={{ backgroundColor: bc.bg, color: bc.c }}>{t(`e4.planBasis.${s.date_basis}`)}</Pill>
                {s.note && <span className="text-[11px] text-[#7A9488]" dir="auto">{s.note}</span>}
              </div>
              {!s.promoted ? (
                <div className="mt-2 flex gap-2">
                  <button data-testid={`e4-plan-action-${s.step_id}`} onClick={() => promote(s.step_id, "action_item")} className="rounded-full border border-[#C4DFD2] px-3 py-1 text-xs font-semibold text-[#2D5A4C] active:scale-95">{t("e4.addAsAction")}</button>
                  <button data-testid={`e4-plan-deadline-${s.step_id}`} onClick={() => promote(s.step_id, "deadline")} className="rounded-full border border-[#C4DFD2] px-3 py-1 text-xs font-semibold text-[#2D5A4C] active:scale-95">{t("e4.addAsDeadline")}</button>
                </div>
              ) : <span className="mt-2 inline-block text-[11px] font-semibold text-[#1B6943]">✓ {t("e4.promoted")}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Drafts ----------------
function DraftsSection({ caseData }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const cid = caseData.id;
  const [type, setType] = useState("REPLY");
  const [facts, setFacts] = useState("");
  const [sentDate, setSentDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [resolved, setResolved] = useState({});
  const [copied, setCopied] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendDate, setSendDate] = useState(new Date().toISOString().slice(0, 10));
  const [followDays, setFollowDays] = useState("14");

  const { data: drafts = [] } = useQuery({
    queryKey: ["drafts", cid, type],
    queryFn: async () => (await api.get(`/cases/${cid}/drafts?draft_type=${type}`)).data,
  });
  const latest = drafts[0];

  const saveEdit = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      await api.post(`/drafts/${latest.id}/revise`, { body_de: editText });
      qc.invalidateQueries({ queryKey: ["drafts", cid, type] });
      qc.invalidateQueries({ queryKey: ["events", cid] });
      setEditing(false);
      toast.success(t("e4.draftBadge"));
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const markSent = async () => {
    setSending(true);
    try {
      await api.post(`/drafts/${latest.id}/mark-sent`, { sent_date: sendDate || null, followup_days: followDays ? Number(followDays) : null });
      qc.invalidateQueries({ queryKey: ["drafts", cid, type] });
      qc.invalidateQueries({ queryKey: ["case", cid] });
      qc.invalidateQueries({ queryKey: ["cases"] });
      qc.invalidateQueries({ queryKey: ["reminders", cid] });
      qc.invalidateQueries({ queryKey: ["events", cid] });
      setShowSend(false);
      toast.success(t("e4.markSent"));
    } catch (e) { toast.error(apiError(e, t)); } finally { setSending(false); }
  };

  const gen = async (resolvedValues) => {
    setBusy(true); setConflicts(null);
    try {
      const body = { type, ui_language: lang, user_facts: facts || null, sent_date: sentDate || null, resolved_values: resolvedValues || null };
      const r = await api.post(`/cases/${cid}/drafts`, body);
      if (r.data.needs_confirmation) { setConflicts(r.data.conflicts); }
      else { qc.invalidateQueries({ queryKey: ["drafts", cid, type] }); qc.invalidateQueries({ queryKey: ["events", cid] }); toast.success(t("e4.draftBadge")); }
    } catch (e) { toast.error(apiError(e, t)); } finally { setBusy(false); }
  };
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { toast.error(t("errors.generic")); } };

  return (
    <div data-testid="e4-drafts" className="mt-4">
      <div className="mb-2 flex gap-2">
        {DRAFT_TYPES.map((tp) => (
          <button key={tp} data-testid={`e4-draft-type-${tp}`} onClick={() => { setType(tp); setConflicts(null); }}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${type === tp ? "bg-[#2D5A4C] text-white" : "border border-[#E2EAE5] bg-white text-[#4A6358]"}`}>
            {t(`e4.draftType.${tp}`)}
          </button>
        ))}
      </div>

      {type === "FOLLOWUP" && (
        <Input data-testid="e4-draft-sentdate" value={sentDate} onChange={(e) => setSentDate(e.target.value)} placeholder={`${t("e4.sentDate")} (TT.MM.JJJJ)`} className="mb-2 h-10 rounded-xl" />
      )}
      <Textarea data-testid="e4-draft-facts" value={facts} onChange={(e) => setFacts(e.target.value)} placeholder={t("e4.userFacts")} rows={3} className="mb-2 rounded-xl" dir="auto" />
      {type === "WIDERSPRUCH" && (
        <p className="mb-2 flex items-start gap-1.5 rounded-xl bg-[#FFF8EC] p-2.5 text-[11px] text-[#B36B00]"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {t("e4.widerspruchHint")}</p>
      )}

      <button data-testid="e4-draft-generate" onClick={() => gen(Object.keys(resolved).length ? resolved : (latest?.based_on?.resolved_values || null))} disabled={busy}
        className="flex items-center gap-1.5 rounded-full bg-[#2D5A4C] px-3.5 py-2 text-sm font-semibold text-white active:scale-95 disabled:opacity-50">
        <Mail size={15} /> {busy ? t("e4.generating") : (latest ? t("e4.regenerate") : t("e4.generate"))}
      </button>

      {conflicts && (
        <div data-testid="e4-draft-conflicts" className="mt-3 rounded-2xl border border-[#FFE2B3] bg-[#FFF8EC] p-3">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-[#B36B00]"><AlertTriangle size={14} /> {t("e4.needsConfirm")}</p>
          {conflicts.map((cf) => (
            <div key={cf.category} className="mb-2">
              <p className="text-[11px] font-semibold text-[#4A6358]">{t(`analysis.cat.${cf.category}`)}</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {cf.values.map((v, i) => (
                  <button key={v} data-testid={`e4-conflict-${cf.category}-opt-${i}`} onClick={() => setResolved((r) => ({ ...r, [cf.category]: v }))}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${resolved[cf.category] === v ? "bg-[#2D5A4C] text-white" : "border border-[#E2EAE5] bg-white text-[#1A2E26]"}`}>{v}</button>
                ))}
              </div>
            </div>
          ))}
          <button data-testid="e4-conflict-apply" onClick={() => gen(resolved)}
            disabled={conflicts.some((c) => !resolved[c.category])}
            className="mt-1 rounded-full bg-[#2D5A4C] px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{t("e4.useValue")}</button>
        </div>
      )}

      {latest && (
        <div data-testid="e4-draft-latest" className="mt-3 rounded-2xl border border-[#E2EAE5] bg-white p-3.5">
          <div className="mb-2 flex items-center justify-between">
            <Pill style={{ backgroundColor: "#FFF8EC", color: "#B36B00" }}>{t("e4.draftBadge")} · {t("e4.version")} {latest.version}{latest.edited ? " ✎" : ""}</Pill>
            <div className="flex gap-1.5">
              <button data-testid="e4-draft-edit" onClick={() => { setEditing(true); setEditText(latest.body_de); }}
                className="flex items-center gap-1 rounded-full border border-[#E2EAE5] px-2.5 py-1 text-[11px] font-semibold text-[#4A6358]">
                <Pencil size={12} /> {t("e4.edit")}
              </button>
              <button data-testid="e4-draft-copy" onClick={() => copy(latest.body_de)} className="flex items-center gap-1 rounded-full border border-[#E2EAE5] px-2.5 py-1 text-[11px] font-semibold text-[#4A6358]">
                {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? t("e4.copied") : t("e4.copy")}
              </button>
            </div>
          </div>
          <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#7A9488]">{t("e4.germanDraft")}</p>
          {editing ? (
            <div className="mb-3">
              <Textarea data-testid="e4-draft-edit-textarea" value={editText} onChange={(e) => setEditText(e.target.value)} rows={12} className="rounded-xl font-sans text-xs" />
              <div className="mt-2 flex gap-2">
                <button data-testid="e4-draft-edit-save" onClick={saveEdit} disabled={saving}
                  className="rounded-full bg-[#2D5A4C] px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{saving ? t("common.saving") : t("e4.saveEdit")}</button>
                <button data-testid="e4-draft-edit-cancel" onClick={() => setEditing(false)}
                  className="rounded-full border border-[#E2EAE5] px-3.5 py-1.5 text-xs font-semibold text-[#4A6358]">{t("common.cancel")}</button>
              </div>
            </div>
          ) : (
            <pre data-testid="e4-draft-body" className="mb-3 whitespace-pre-wrap rounded-xl bg-[#F8FAF8] p-3 font-sans text-xs leading-relaxed text-[#1A2E26]">{latest.body_de}</pre>
          )}
          {latest.translation && (
            <>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#7A9488]">{t("e4.translation")}</p>
              <p className="whitespace-pre-wrap text-xs text-[#4A6358]" dir="auto">{latest.translation}</p>
            </>
          )}
          {!editing && (
            latest.status === "SENT" ? (
              <div data-testid="e4-draft-sent-state" className="mt-3 flex items-center gap-2 rounded-xl bg-[#EDF7F2] px-3 py-2 text-xs font-semibold text-[#1B6943]">
                <Check size={14} /> {t("e4.sentOn")} {fmtDate(latest.sent_date)}
              </div>
            ) : (
              <div className="mt-3">
                {!showSend ? (
                  <button data-testid="e4-draft-marksent" onClick={() => setShowSend(true)}
                    className="flex items-center gap-1.5 rounded-full border border-[#C4DFD2] px-3 py-1.5 text-xs font-semibold text-[#2D5A4C] active:scale-95">
                    <Send size={13} /> {t("e4.markSent")}
                  </button>
                ) : (
                  <div data-testid="e4-draft-marksent-form" className="rounded-xl border border-[#E2EAE5] p-3">
                    <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-[#7A9488]">{t("e4.sentDate")}</label>
                    <input data-testid="e4-marksent-date" type="date" value={sendDate} onChange={(e) => setSendDate(e.target.value)}
                      className="mb-2 h-10 w-full rounded-xl border border-[#E2EAE5] px-3 text-sm" />
                    <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-[#7A9488]">{t("e4.followupAfter")}</label>
                    <input data-testid="e4-marksent-days" type="number" min="0" value={followDays} onChange={(e) => setFollowDays(e.target.value)}
                      className="mb-3 h-10 w-24 rounded-xl border border-[#E2EAE5] px-3 text-sm" />
                    <div className="flex gap-2">
                      <button data-testid="e4-marksent-confirm" onClick={markSent} disabled={sending}
                        className="rounded-full bg-[#2D5A4C] px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{sending ? t("common.saving") : t("e4.markSent")}</button>
                      <button data-testid="e4-marksent-cancel" onClick={() => setShowSend(false)}
                        className="rounded-full border border-[#E2EAE5] px-3.5 py-1.5 text-xs font-semibold text-[#4A6358]">{t("common.cancel")}</button>
                    </div>
                  </div>
                )}
              </div>
            )
          )}
          {drafts.length > 1 && (
            <button data-testid="e4-draft-history-toggle" onClick={() => setShowHistory((s) => !s)} className="mt-3 flex items-center gap-1 text-[11px] font-semibold text-[#2D5A4C]">
              <ChevronDown size={13} className={showHistory ? "rotate-180 transition" : "transition"} /> {t("e4.history")} ({drafts.length - 1})
            </button>
          )}
          {showHistory && drafts.slice(1).map((d) => (
            <div key={d.id} data-testid={`e4-draft-version-${d.version}`} className="mt-2 rounded-xl border border-[#F0F4F1] p-2.5">
              <p className="text-[11px] font-semibold text-[#7A9488]">{t("e4.version")} {d.version}</p>
              <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap font-sans text-[11px] text-[#4A6358]">{d.body_de}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: "docs", icon: FileCheck2, key: "reqDocs" },
  { id: "plan", icon: CalendarClock, key: "plan" },
  { id: "drafts", icon: Mail, key: "drafts" },
];

export function ResolutionAssistant({ caseData, initialTab }) {
  const { t } = useI18n();
  const validTab = ["docs", "plan", "drafts"].includes(initialTab) ? initialTab : "docs";
  const [tab, setTab] = useState(validTab);
  const rootRef = useRef(null);
  useEffect(() => {
    if (initialTab && rootRef.current) {
      const id = setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
      return () => clearTimeout(id);
    }
  }, [initialTab]);
  return (
    <div ref={rootRef} data-testid="resolution-assistant" className="mt-8 scroll-mt-4">
      <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">{t("e4.title")}</h3>
      <div className="flex gap-2 rounded-2xl bg-[#F0F4F1] p-1">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          return (
            <button key={tb.id} data-testid={`e4-tab-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold transition-all ${tab === tb.id ? "bg-white text-[#2D5A4C] shadow-sm" : "text-[#7A9488]"}`}>
              <Icon size={14} /> {t(`e4.${tb.key}`)}
            </button>
          );
        })}
      </div>
      {tab === "docs" && <RequiredDocs caseData={caseData} />}
      {tab === "plan" && <PlanSection caseData={caseData} />}
      {tab === "drafts" && <DraftsSection caseData={caseData} />}
    </div>
  );
}
